import { getJson, sendJson } from '@/lib/api';
import type { ShipmentRow, ThreadMessage } from '@bcecloud/portal-ui';
import type { RichTextDoc } from '@bcecloud/portal-ui';

/**
 * The client's event workspace, read and written from BCE's side.
 *
 * Every call goes through `/events/:id/workspace/...`, which mounts the
 * portal's own handlers for staff on the tenant whose Salesforce join covers
 * the event. The shapes here are the portal's shapes: the client and BCE are
 * looking at the same records.
 */

export interface WorkspaceHeader {
  readonly id: string;
  readonly source: 'BOOKING' | 'EVENT';
  readonly programName: string | null;
  readonly reference: string | null;
  readonly startAt: string | null;
  readonly eventId: string | null;
}

export interface PlanningTask {
  readonly id: string;
  readonly title: string;
  readonly description: string | null;
  readonly responsibleParty: string | null;
  readonly responsibleName: string | null;
  readonly dueAt: string | null;
  readonly status: string;
  readonly approvalRequired: boolean;
  readonly notes: string | null;
  readonly nextAction: string | null;
  readonly responsibilityKey: string | null;
  readonly section: string | null;
  readonly overdue: boolean;
}

export type ChecklistKind = 'PLAN' | 'VENUE';

export interface WorkspaceChecklists {
  readonly tasks: readonly PlanningTask[];
  readonly responsibilities: readonly PlanningTask[];
  readonly progress: { readonly total: number; readonly settled: number; readonly overdue: number; readonly share: number };
}

export interface NewPlanningTask {
  readonly list: ChecklistKind;
  readonly title: string;
  readonly section?: string;
  readonly responsibleParty?: string;
  readonly responsibleName?: string;
  readonly dueAt?: string;
  readonly notes?: string;
}

export interface TaskChanges {
  readonly title?: string;
  readonly section?: string | null;
  readonly status?: string;
  readonly responsibleParty?: string | null;
  readonly responsibleName?: string;
  readonly dueAt?: string | null;
  readonly notes?: string;
}

export interface PlannedRoom {
  readonly name: string;
  readonly dimensions?: string;
  readonly purpose?: string;
  readonly diagramUrl?: string;
}

export interface PlannedVenue {
  readonly name: string;
  readonly address?: string;
  readonly contactName?: string;
  readonly contactPhone?: string;
  readonly rooms: readonly PlannedRoom[];
}

export interface RoomPlan {
  readonly venues?: readonly PlannedVenue[] | null;
  readonly teamCount?: number | null;
  readonly teamSize?: number | null;
  readonly tablesRequired?: number | null;
  readonly chairsRequired?: number | null;
  readonly facilitatorPosition?: string | null;
  readonly materialsDistributionArea?: string | null;
  readonly donationStagingArea?: string | null;
  readonly accessibilityNotes?: string | null;
  readonly setupBreakdownNotes?: string | null;
  readonly diagramNotes?: string | null;
  readonly updatedByName?: string | null;
}

export interface WorkspaceChange {
  readonly id: string;
  readonly summary: string;
  readonly detail: string | null;
  readonly previousValue: string | null;
  readonly newValue: string | null;
  readonly actorName: string | null;
  readonly critical: boolean;
  readonly acknowledgedByName: string | null;
  readonly acknowledgedAt: string | null;
  readonly createdAt: string;
}

export interface RoomAndChanges {
  readonly roomPlan: RoomPlan | null;
  readonly changes: readonly WorkspaceChange[];
  readonly unacknowledged: number;
}

/** The receiving, freight and parking plan (§29–31). */
export interface MaterialsPlan {
  readonly receivingContactName?: string | null;
  readonly receivingContactPhone?: string | null;
  readonly receivingAddress?: string | null;
  readonly dockHours?: string | null;
  readonly storageLocation?: string | null;
  readonly handlingInstructions?: string | null;
  readonly receivingFees?: string | null;
  readonly disposalPlan?: string | null;
  readonly boxCount?: number | null;
  readonly palletCount?: number | null;
  readonly crateCount?: number | null;
  readonly roomAssignments?: string | null;
  readonly returnFreightPlan?: string | null;
  readonly contingencyPlan?: string | null;
  readonly staffVehicleCount?: number | null;
  readonly staffArrivalTime?: string | null;
  readonly vehicleAccessNotes?: string | null;
  readonly parkingInstructions?: string | null;
  readonly updatedByName?: string | null;
}

export interface MaterialsView {
  readonly shipments: readonly ShipmentRow[];
  readonly plan: MaterialsPlan | null;
  readonly awaitingConfirmation: number;
}

export interface PortalMember {
  readonly id: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string;
  readonly isActive: boolean;
}

export interface NewMessage {
  readonly content: RichTextDoc;
  readonly parentId?: string;
}

export type WorkspaceMessage = ThreadMessage;

function base(eventId: string): string {
  return `/events/${eventId}/workspace`;
}

export const readEventWorkspace = (eventId: string, signal?: AbortSignal): Promise<WorkspaceHeader> =>
  getJson<WorkspaceHeader>(base(eventId), undefined, signal);

export const listWorkspaceMessages = (eventId: string, signal?: AbortSignal): Promise<WorkspaceMessage[]> =>
  getJson<WorkspaceMessage[]>(`${base(eventId)}/messages`, undefined, signal);

export const postWorkspaceMessage = (eventId: string, message: NewMessage): Promise<WorkspaceMessage> =>
  sendJson<WorkspaceMessage>('post', `${base(eventId)}/messages`, message);

export const listWorkspaceMembers = (eventId: string, signal?: AbortSignal): Promise<{ members: PortalMember[] }> =>
  getJson<{ members: PortalMember[] }>(`${base(eventId)}/members`, undefined, signal);

/** Whose planning checklist: an event's workspace or a campaign's. Both live on the client's portal under the same handlers. */
export type ChecklistScope = { readonly kind: 'event' | 'campaign'; readonly id: string };

/** The scope's key for caches: the two kinds never share an id space, so the pair is enough. */
export const checklistScopeKey = (scope: ChecklistScope): string => `${scope.kind}:${scope.id}`;

function checklistBase(scope: ChecklistScope): string {
  return scope.kind === 'campaign' ? `/campaigns/${scope.id}/workspace` : base(scope.id);
}

export const readWorkspaceChecklists = (scope: ChecklistScope, signal?: AbortSignal): Promise<WorkspaceChecklists> =>
  getJson<WorkspaceChecklists>(`${checklistBase(scope)}/checklists`, undefined, signal);

export const addWorkspaceTask = (scope: ChecklistScope, task: NewPlanningTask): Promise<PlanningTask> =>
  sendJson<PlanningTask>('post', `${checklistBase(scope)}/tasks`, task);

export const updateWorkspaceTask = (scope: ChecklistScope, taskId: string, changes: TaskChanges): Promise<PlanningTask> =>
  sendJson<PlanningTask>('patch', `${checklistBase(scope)}/tasks/${taskId}`, changes);

export const deleteWorkspaceTask = (scope: ChecklistScope, taskId: string): Promise<void> =>
  sendJson<void>('delete', `${checklistBase(scope)}/tasks/${taskId}`);

export const readWorkspaceRoom = (eventId: string, signal?: AbortSignal): Promise<RoomAndChanges> =>
  getJson<RoomAndChanges>(`${base(eventId)}/room`, undefined, signal);

export const saveWorkspaceRoom = (eventId: string, changes: Partial<RoomPlan>): Promise<RoomPlan> =>
  sendJson<RoomPlan>('patch', `${base(eventId)}/room`, changes);

export const readWorkspaceMaterials = (eventId: string, signal?: AbortSignal): Promise<MaterialsView> =>
  getJson<MaterialsView>(`${base(eventId)}/materials`, undefined, signal);

export const saveWorkspaceMaterials = (eventId: string, changes: Partial<MaterialsPlan>): Promise<MaterialsPlan> =>
  sendJson<MaterialsPlan>('patch', `${base(eventId)}/materials`, changes);
